import {useTranslation} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC} from 'react';
import sc, {css} from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {Icon} from '../../../../atoms/Icon/Icon';
import {FilterFlyoutIconName} from '../../assets';

interface Props {
  activeFilters: Array<{id: string; label: string; slug: string}>;
  label: string;
  onButtonClick: ({id, slug}: {id: string; slug: string}) => void;
  resetAllFilters: () => void;
  enableFlyoutFilter: boolean;
}

export const FilterFlyoutAppliedFilters: FC<Props> = ({
  activeFilters,
  label,
  onButtonClick,
  resetAllFilters,
  enableFlyoutFilter,
}) => {
  const {t} = useTranslation();

  const filtersSet = activeFilters.length > 0;

  return (
    <div data-testid="filterFlyoutSectionAppliedFilters">
      <StyledFilterCard
        onReset={resetAllFilters}
        resetLabel={filtersSet ? t('Reset all') : undefined}
        title={label}
        disableHorizontalPadding={enableFlyoutFilter}
      >
        <Buttons
          $largerMarginTop={!enableFlyoutFilter}
          $withoutFlexWrap={enableFlyoutFilter}
        >
          {filtersSet ? (
            activeFilters.map(filter => {
              return (
                <Button
                  data-testid={`filterFlyoutSectionAppliedFiltersDelete-${filter.id}`}
                  key={`${filter.slug}-${filter.id}`}
                  onClick={() => {
                    onButtonClick({id: filter.id, slug: filter.slug});
                  }}
                  $largerBorderRadius={enableFlyoutFilter}
                >
                  {filter.label}
                  <DeleteIcon
                    asset={FilterFlyoutIconName.Delete}
                    color={COLORS.Grey1}
                  />
                </Button>
              );
            })
          ) : (
            <Placeholder
              data-testid={`filterFlyoutSectionNoAppliedFiltersPlaceholder`}
            >
              {t('No filter selected')}
            </Placeholder>
          )}
        </Buttons>
      </StyledFilterCard>
    </div>
  );
};

const Buttons = sc.div<{
  $largerMarginTop: boolean;
  $withoutFlexWrap?: boolean;
}>`
  display: flex;
  ${props =>
    props.$withoutFlexWrap ? 'overflow-y: auto;' : 'flex-wrap: wrap;'}
  margin-top: ${props => (props.$largerMarginTop ? '1.25rem' : '0.5rem')};
`;

const StyledFilterCard = sc(FilterCard)`
  border-bottom: unset;
`;

const sharedButtonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  white-space: nowrap;
  padding: 0 16px;
  border: 1px solid ${COLORS.Grey3};
  border-radius: 2px;
  background-color: ${COLORS.White};
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  margin-top: 5px;
  flex-grow: 0;
`;

const Button = sc.button<{$largerBorderRadius?: boolean}>`
  ${sharedButtonStyles};
  cursor: pointer;
  ${props => (props.$largerBorderRadius ? 'border-radius:4px;' : '')}

  &:not(:last-child) {
    margin-right: 10px;
  }
`;

const Placeholder = sc.div`
  ${sharedButtonStyles};
`;

const DeleteIcon = linariaStyled(Icon)`
  margin-left: 10px;
  width: 1.0625rem;
  height: 1.0625rem;
`;
